import * as THREE from 'three';
import { colors } from '@/utils/colors';
import type { ClubHeadShape, ClubSpec } from '@/physics/swingModel';
import {
  FACE_OFFSET,
  buildGrip,
  buildHead,
  buildShaft,
  makeGhostMaterials,
  makeSolidMaterials,
  type HeadBuild,
} from './clubGeometry';

export type ClubOverlayOptions = {
  /** Shaft tint. */
  shaftColor?: THREE.ColorRepresentation;
  headColor?: THREE.ColorRepresentation;
  /** Length of the ground alignment line, metres. */
  alignmentLength?: number;
  /**
   * Which hand the club is in. Only the head's toe-ward bias depends on it —
   * the mass of the head sits on the far side of the shaft from the player.
   */
  clubHand?: 'left' | 'right';
  /**
   * Build as a translucent outline instead of a solid club. Used for the
   * alignment guide the player lines their physical attachment up with — it has
   * to read as "put your club here", not as a second club.
   */
  ghost?: boolean;
};

const MIN_DRAWN_LENGTH = 0.3;

/**
 * The club drawn on the swinging controller, Golf+ style — procedural, like the
 * anchor reticle and aim arrow, so the mode ships without new binary assets.
 * The meshes themselves come from `clubGeometry`; this class owns posing,
 * calibration, materials and lifetime.
 *
 * Everything is built in one canonical frame whose ORIGIN IS THE BUTT of the
 * grip: shaft down -Z, face normal +X, heel toward +Y for a right-hander. The
 * calibrated basis (`setGeometry`) then places and rotates the whole assembly
 * inside the controller's grip space — the butt can sit anywhere relative to
 * the controller, which is what a handle attachment with an offset mount needs.
 *
 * One-piece by construction: each head splits into a FIXED hosel assembly —
 * ferrule + hosel + neck, coaxial with the shaft axis, so the shaft junction
 * is welded at every loft — and a loftGroup carrying body, face and details
 * with the shape's nominal loft pre-baked into the geometry. `setClub` only
 * rotates the loftGroup by the small residual (actual − nominal) about the
 * face centre, so loft can never unweld the hosel. (The previous build rotated
 * the entire head group by the loft and hung the hosel off-axis at the heel —
 * at wedge lofts the head visibly detached from the shaft.)
 *
 * The head-group origin is the face centre — the impact point the swing
 * controls sweep through the ball — and it lies on the residual-rotation axis,
 * so what you see hit the ball is exactly what the physics measured.
 *
 * Materials are deliberately plain (never handed to `WorldMask.applyToMaterial`)
 * and every mesh is `mrMaskExempt`: the club lives in the player's hands, inside
 * the passthrough bay, where the portal mask would otherwise cut it away.
 */
export class ClubOverlay {
  object: THREE.Group;

  #club: THREE.Group;
  #shaft: THREE.Mesh;
  #heads = new Map<ClubHeadShape, HeadBuild>();
  #alignment: THREE.Group;
  /** +1 puts the heel on canonical +Y (right-handed), -1 mirrors it. */
  #heelSign: 1 | -1;

  /** Geometry as calibrated, kept so the ground slide composes with it. */
  #buttBase = new THREE.Vector3();
  #shaftDir = new THREE.Vector3(0, 0, -1);
  #slide = 0;

  // scratch
  #basis = new THREE.Matrix4();
  #axisX = new THREE.Vector3();
  #axisY = new THREE.Vector3();
  #axisZ = new THREE.Vector3();
  #dir = new THREE.Vector3();
  #scratch = new THREE.Vector3();

  constructor(options: ClubOverlayOptions = {}) {
    this.#heelSign = options.clubHand === 'left' ? -1 : 1;
    const ghost = !!options.ghost;

    this.object = new THREE.Group();
    this.object.name = ghost ? 'ClubOverlayGhost' : 'ClubOverlay';
    this.object.userData.mrMaskExempt = true;

    // One material set for the whole club. Created here and disposed by our
    // traverse-dispose, so this class stays the material owner — the
    // clubGeometry builders only ever borrow them.
    const materials = ghost
      ? makeGhostMaterials()
      : makeSolidMaterials({ shaftColor: options.shaftColor, headColor: options.headColor });

    // Posed from the controller's grip space every frame; the club inside it
    // carries the calibrated basis and butt offset.
    const gripGroup = new THREE.Group();
    gripGroup.name = 'ClubGrip';
    this.#club = new THREE.Group();
    gripGroup.add(this.#club);
    this.object.add(gripGroup);
    this.#club.visible = false;

    // Fixed-size grip at the canonical origin; unit-length shaft along -Z that
    // `setGeometry` stretches butt → head origin via scale.z, so the assembly
    // stays one piece at any calibrated length.
    this.#shaft = buildShaft(materials);
    this.#club.add(buildGrip(materials), this.#shaft);

    for (const shape of ['wood', 'iron', 'wedge'] as const) {
      const parts = buildHead(shape, materials, this.#heelSign, ghost ? 'ghost' : 'full');
      parts.group.visible = shape === 'iron';
      this.#heads.set(shape, parts);
      this.#club.add(parts.group);
    }

    this.#alignment = this.#buildAlignment(options.alignmentLength ?? 2.2);
    this.object.add(this.#alignment);

    if (ghost) {
      // Over the world and the hint, under the wrist menu, so the outline is
      // never hidden behind the turf or the player's own hands
      this.#club.traverse(child => { child.renderOrder = 36; });
    }

    // Sensible default until the first setGeometry
    this.setGeometry(
      new THREE.Vector3(),
      new THREE.Vector3(0, 0, -1),
      new THREE.Vector3(0, 0, -1),
      new THREE.Vector3(1, 0, 0),
    );
  }

  /**
   * Show/hide the club. Deliberately independent of the alignment line: this is
   * called every frame from the controller's tracking state, and a momentary
   * dropout must not take the ground line with it permanently.
   */
  setVisible(visible: boolean) {
    this.#club.visible = visible;
  }

  get isVisible(): boolean {
    return this.#club.visible;
  }

  /** Drive the club from a grip-space pose in reference space. */
  setPose(position: THREE.Vector3, quaternion: THREE.Quaternion) {
    const gripGroup = this.#club.parent!;
    gripGroup.position.copy(position);
    gripGroup.quaternion.copy(quaternion);
  }

  /**
   * Adopt a measured (or preset) geometry, all in grip space: where the top of
   * the grip sits relative to the controller, where the head (face centre) is,
   * WHICH WAY THE SHAFT RUNS, and which way a square face points. The shaft
   * direction is a separate parameter because the face centre no longer lies on
   * the shaft line — it hangs FACE_OFFSET toe-ward of it (the shaft enters a
   * real club at the heel) — so `normalize(head − butt)` would tilt the shaft.
   * The drawn shaft stretches along `shaftLocal` by the along-shaft distance,
   * and each head group hangs off it laterally so its in-head neck lands back
   * exactly on the drawn shaft axis.
   */
  setGeometry(
    buttLocal: THREE.Vector3,
    headLocal: THREE.Vector3,
    shaftLocal: THREE.Vector3,
    faceLocal: THREE.Vector3,
  ) {
    if (shaftLocal.lengthSq() < 1e-6) return;
    this.#dir.copy(shaftLocal).normalize();
    // Along-shaft distance to the hosel — the lateral face offset is carried by
    // the head group's own position, not by the shaft length
    const length = Math.max(
      this.#scratch.subVectors(headLocal, buttLocal).dot(this.#dir),
      MIN_DRAWN_LENGTH,
    );

    this.#axisZ.copy(this.#dir).negate();
    this.#axisX.copy(faceLocal).projectOnPlane(this.#axisZ);
    if (this.#axisX.lengthSq() < 1e-6) return;
    this.#axisX.normalize();
    this.#axisY.crossVectors(this.#axisZ, this.#axisX).normalize();

    this.#basis.makeBasis(this.#axisX, this.#axisY, this.#axisZ);
    this.#club.quaternion.setFromRotationMatrix(this.#basis);
    this.#buttBase.copy(buttLocal);
    this.#shaftDir.copy(this.#dir);
    this.#applySlide();
    this.#shaft.scale.z = length;
    // Face centre (group origin) sits FACE_OFFSET toe-ward (−Y·heelSign in the
    // club frame) of the shaft line, per shape — the heel-mounted neck inside
    // each head then lands exactly on the drawn shaft.
    for (const [shape, { group }] of this.#heads) {
      group.position.set(0, -this.#heelSign * FACE_OFFSET[shape], -length);
    }
  }

  /**
   * Slide the whole drawn club up its shaft line (metres) — the Golf+ ground
   * rule. Grip, shaft and head all ride along, so the club appears to slide
   * through the hands rather than clip into the floor. Composes with whatever
   * geometry was last set, so calibration updates and sliding never fight.
   */
  setSlide(meters: number) {
    if (meters === this.#slide) return;
    this.#slide = meters;
    this.#applySlide();
  }

  #applySlide() {
    this.#club.position.copy(this.#buttBase).addScaledVector(this.#shaftDir, -this.#slide);
  }

  /** Swap the head shape and present its loft. */
  setClub(club: ClubSpec) {
    for (const [shape, parts] of this.#heads) {
      parts.group.visible = shape === club.head;
      // Only the selected head's loftGroup rotates (by the residual over its
      // baked nominal, about the face centre) — the hosel assembly stays
      // welded to the shaft axis, which is what killed the old wedge-loft
      // shear-off. Hidden shapes keep their last loft: they are always re-set
      // here before they can become visible, and lofting them by another
      // shape's loft would only trip the nominal-drift warning for nothing.
      if (shape === club.head) parts.applyLoft(club.loft);
    }
  }

  /** Pin the ground target line to the anchored tee. */
  placeAlignment(position: THREE.Vector3, yaw: number) {
    this.#alignment.position.copy(position);
    this.#alignment.position.y += 0.004; // clear of the floor / fairway plane
    this.#alignment.rotation.y = yaw;
  }

  showAlignment(visible: boolean) {
    this.#alignment.visible = visible;
  }

  /** Target line on the floor plus a tick across it at the ball. */
  #buildAlignment(length: number): THREE.Group {
    const group = new THREE.Group();
    group.name = 'ClubAlignment';
    group.userData.mrMaskExempt = true;
    group.visible = false;

    const material = new THREE.MeshBasicMaterial({
      color: colors.primary ?? '#21d48d',
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      fog: false,
      toneMapped: false,
    });

    const line = new THREE.Mesh(
      new THREE.PlaneGeometry(0.02, length).rotateX(-Math.PI / 2).translate(0, 0, length / 2),
      material
    );
    const tick = new THREE.Mesh(
      new THREE.PlaneGeometry(0.34, 0.02).rotateX(-Math.PI / 2),
      material
    );
    for (const mesh of [line, tick]) {
      mesh.renderOrder = 3; // over the turf, under the trail
      mesh.userData.mrMaskExempt = true;
      group.add(mesh);
    }
    return group;
  }

  dispose() {
    this.object.removeFromParent();
    this.object.traverse((child: any) => {
      child.geometry?.dispose?.();
      const materials = Array.isArray(child.material) ? child.material : child.material ? [child.material] : [];
      for (const material of materials) material.dispose?.();
    });
  }
}
